import {
  Breadcrumbs,
  Card,
  Typography,
  Grid,
  Input,
  Stack,
  FormLabel,
  Box,
  CircularProgress,
  Tab,
  Tabs,
  TabPanel,
  TabList,
} from '@mui/joy';
import { ArticleImages } from '@/components/Pages/Articles/ArticleImages';
import { ArticleStockModal } from '@/components/Pages/Articles/ArticleStockModal';
import { AttachMoney as AttachMoneyIcon } from '@mui/icons-material';
import { CreateEditArticleModal } from '@/components/Pages/Articles/CreateEditArticleModal';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { pageTitle, usePageTitle } from '@/utils/pageTitle';
import { DeleteArticleModal } from '@/components/Pages/Articles/DeleteArticleModal';
import { PrintArticleLabelsModal } from '@/components/Pages/Articles/PrintArticleLabelsModal';
import { StockMovementsTable } from '@/components/Pages/Articles/StockMovementsTable';
import { Table, createColumn } from '@/components/UI/Table';
import { useMemo } from 'react';
import { useArticle } from '@/hooks/useArticles';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/articles/$id')({
  component: ArticleDetail,
  head: () => pageTitle('Artículos'),
});

function ArticleDetail() {
  const { id } = Route.useParams();
  const { hasClaim, isLoading: authLoading } = useAuth();
  const canRead = hasClaim('ARTICLES_READ');
  const canWrite = hasClaim('ARTICLES_WRITE');

  const { data: article, isLoading } = useArticle(id);
  const isArticle = useMemo(() => article?.type === 'article', [article]);
  usePageTitle('Artículos', article?.name);

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/articles' />;
  }

  if (!article) {
    return <div>Artículo no encontrado</div>;
  }

  return (
    <>
      <Stack direction='row' justifyContent='space-between' sx={{ mb: 2 }}>
        <Breadcrumbs size='sm'>
          <Link to='/articles'>Artículos</Link>
          <Typography>{article?.name}</Typography>
        </Breadcrumbs>

        <Stack direction='row' gap={1}>
          {isArticle && <PrintArticleLabelsModal article={article} />}
          {canWrite && (
            <>
              <CreateEditArticleModal article={article} isService={!isArticle} />
              <DeleteArticleModal
                id={article?.id.toString()}
                name={article?.name}
              />
              {isArticle && <ArticleStockModal articleId={article.id} />}
            </>
          )}
        </Stack>
      </Stack>

      <Tabs>
        <TabList tabFlex={1}>
          <Tab variant='plain' color='neutral'>
            Atributos
          </Tab>
          {isArticle && (
            <Tab variant='plain' color='neutral'>
              Stock
            </Tab>
          )}
          <Tab variant='plain' color='neutral'>
            Imágenes
          </Tab>
        </TabList>

        <TabPanel value={0}>
          <Card>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid xs={12} md={6}>
                <FormLabel>Nombre</FormLabel>
                <Input
                  value={article?.name}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <FormLabel>SKU</FormLabel>
                <Input
                  value={article?.sku}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <FormLabel>Categoría</FormLabel>
                <Input
                  value={article?.categoryName}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
              {isArticle && (
                <Grid xs={12} md={6}>
                  <FormLabel>Precio Compra</FormLabel>
                  <Input
                    value={article?.priceBuy ?? 0}
                    startDecorator={<AttachMoneyIcon />}
                    readOnly
                    variant='soft'
                    size='sm'
                    sx={{ backgroundColor: 'background.body' }}
                  />
                </Grid>
              )}
              <Grid xs={12} md={6}>
                <FormLabel>Precio Venta</FormLabel>
                <Input
                  value={article?.priceSell ?? 0}
                  startDecorator={<AttachMoneyIcon />}
                  readOnly
                  variant='soft'
                  size='sm'
                  sx={{ backgroundColor: 'background.body' }}
                />
              </Grid>
            </Grid>
          </Card>
        </TabPanel>

        {isArticle && (
          <TabPanel value={1}>
            <Stack gap={3}>
              <Card>
                <Box sx={{ mb: 2 }}>
                  <Typography level='h4'>Stock</Typography>
                </Box>

                <Table
                  data={article.warehouseStock || []}
                  columns={[
                    createColumn('warehouseName', 'Sucursal', 'warehouseName'),
                    createColumn('stock', 'Stock', 'stock'),
                  ]}
                  stripe='odd'
                />
              </Card>

              <StockMovementsTable id={id} />
            </Stack>
          </TabPanel>
        )}

        <TabPanel value={isArticle ? 2 : 1}>
          <ArticleImages article={article} />
        </TabPanel>
      </Tabs>
    </>
  );
}
